import React, {useState} from 'react';
import styles from './EledoPdfEditor.module.scss';
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import classnames from "classnames";
import {saveTemplate} from "_reducers/templateReducer";
import {EledoModal, EledoFormGenerator} from "components/library";
import {EledoLabel} from "components/library/EledoFormsComponents";
import {PdfEditorModalFormModel as formModel} from "model";
import {useForm, FormProvider} from "react-hook-form";
import utils from "utils";

const EledoPdfEditor = () => {
  const [editedTemplate, activePageId] = useSelector(state => [
    state.templates.editedTemplate,
    state.templates.activePageId
  ], shallowEqual);
  const [modalDisplayed, setModalDisplayed] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [editedField, setEditedField] = useState(null);

  const dispatch = useDispatch();

  const handleModalClosed = () => {
    setModalDisplayed(false);
    setSelectedPage(null);
    setSelectedField(null);
  };

  const handleModalConfirmed = async data => {
    const newTemplate = utils.deepCopy(editedTemplate);
    const newField = newTemplate.documents[activePageId].innerDocument.pdFields[selectedField.id];
    newField.expression = data.expression;
    newField.size = data.size > 0 ? data.size : null;
    newField.align = data.align;
    newField.multiline = data.multiline;
    const newWidget = newTemplate.documents[activePageId].innerDocument.pages[selectedPage].widgets
      .find(w => w.fieldId === selectedField.id && w.page === selectedPage && w.idx === selectedWidget.idx);
    newWidget.font = data.font;
    newWidget.size = data.size > 0 ? data.size : null;
    newWidget.color = data.color;
    // eslint-disable-next-line no-unused-vars
    const response = await dispatch(saveTemplate(newTemplate));
    handleModalClosed();
  };

  const handleFieldClicked = (page, field, widget) => {
    setSelectedPage(page);
    setSelectedField(field);
    setSelectedWidget(widget);
    setEditedField({
      fieldId: field.id,
      page: widget.page,
      widgetIdx: widget.idx,
      desc: field.desc,
      expression: field.expression,
      font: widget.font,
      size: Math.round(widget.size || field.size),
      color: widget.color,
      align: field.align,
      multiline: field.multiline,
      valueType: field.valueType,
      options: field.options
    });
    setModalDisplayed(true);
  };

  return (
    <div className={classnames(styles.EledoPdfEditor, "cke_contents_wrapper")} data-testid="EledoPdfEditor">
      {editedTemplate.documents[activePageId].innerDocument.pages.map((page, pageId) => {
        return (
        <div style={{position: "relative", marginLeft: "auto", marginRight: "auto",
                     width: `${page.width}px`, height: `${page.height}px`}} key={`page-${page.index}`}>
          <img loading="lazy"
               alt={`Page ${pageId+1} background`}
               src={page.thumbnailUrl}/>
          { page.widgets && Object.values(page.widgets).map((widget, index) =>
            {
              const field = Object.values(editedTemplate.documents[activePageId].innerDocument.pdFields).find(f => f.id === widget.fieldId);
              
              return (
                <div data-field-id={field.id} data-field-desc={field.desc}
                    key={`${pageId}-${field.id}-${widget.idx}-${index}`}
                    className={styles.teField}
                    style={{top: `${widget.box.y}px`, left: `${widget.box.x}px`,
                            width: `${widget.box.width}px`, height: `${widget.box.height}px`,
                            lineHeight: `${widget.box.height}px`,
                            fontFamily: widget.font,
                            fontSize: widget.size > 0 ? widget.size + "px" : "inherit",
                            color: widget.color,
                            // keep == as the select component sets align as text instead of number, TODO: fix select dropdown to support numbers
                            textAlign: field.align === 2 ? "right" : field.align === 1 ? "center" : "left"
                          }}
                    onClick={(e) => handleFieldClicked(pageId, field, widget)}>
                  {field.expression}
                </div>
              )
            })
          }
        </div>
      )})}
      {modalDisplayed && selectedField && editedField &&
        <FieldModalForm
          editedTemplate={editedTemplate}
          modalDisplayed={modalDisplayed}
          selectedField={selectedField}
          editedField={editedField}
          selectedWidget={selectedWidget}
          handleModalClosed={handleModalClosed}
          handleModalConfirmed={handleModalConfirmed}
        />
      }
    </div>
  )
};

const FieldModalForm = ({editedTemplate, modalDisplayed, selectedField, editedField, selectedWidget, handleModalClosed, handleModalConfirmed}) => {
  const formHandler = useForm({mode: "onBlur"});
  //!!the following two lines need to be here to activate the hook-form:
  // eslint-disable-next-line no-unused-vars
  const { formState: {isValid}, watch } = formHandler;

  const modalContent = (
    <FormProvider {...formHandler}>
      <form>
        <fieldset>
          <EledoLabel id="field-desc" label="Field Description (from PDF)"/>
          <input id="field-desc" style={{cursor: "initial"}}
                 value={`${selectedField?.desc}`}
                 disabled={true}/>
        </fieldset>
        {editedField && <EledoFormGenerator formModel={formModel}
                                            editedDataObject={editedField}
                                            variables={editedTemplate.derived.variables || []}/>}
        <fieldset>
          <EledoLabel id="field-type" label="Data Type"/>
          <input id="field-desc" style={{cursor: "initial"}}
                 value={`${selectedField?.valueType}`}
                 disabled={true}/>

          { selectedField.options && selectedField.options.length > 0 &&
            <>
              <EledoLabel id="field-options" label="Options"/>
              <input id="field-options" style={{cursor: "initial"}}
                  value={`${selectedField?.options}`}
                  disabled={true}/>
            </>
          }
        </fieldset>
      </form>
    </FormProvider>
  );

  const modalActions = (
    <React.Fragment>
      <button className="button red grow w-button"
              onClick={handleModalClosed}>
        Close
      </button>

      <button className="button grow w-button"
              onClick={formHandler.handleSubmit(handleModalConfirmed)}>
        OK
      </button>
    </React.Fragment>
  );

  return <React.Fragment>
    <EledoModal title="Edit PDF form field"
                content={(modalDisplayed && selectedField) ? modalContent : null}
                displayed={modalDisplayed}
                actions={modalActions}
                onDismiss={handleModalClosed}/>
  </React.Fragment>
};

export default EledoPdfEditor;
